import { gql, useMutation } from '@apollo/client';
import React from 'react';
import Form, { Field, Submit } from '../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import USER_AVATAR_FRAGMENT from '../graphql/fragments/UserAvatar';
import type {
  RequestVerificationModal_PostFragment,
  RequestVerificationMutation,
  RequestVerificationMutationVariables,
} from '../graphql/generated/graphqlTypes';
import UserRow from '../user/UserRow';
import styles from './RequestVerificationModal.module.scss';
import type { VerificationFormValues } from './VerificationFrequencyFields';
import VerificationFrequencyFields, {
  FREQUENCIES,
  validateVerificationFrequencyFields,
} from './VerificationFrequencyFields';
import VerifiedIcon from './VerifiedIcon';

const POST_FRAGMENT = gql`
  fragment RequestVerificationModal_post on Post {
    id
    owner {
      id
      name
      title
      ...UserAvatar
    }
  }
  ${USER_AVATAR_FRAGMENT}
`;

const REQUEST_VERIFICATION_MUTATION = gql`
  mutation requestVerification(
    $postId: ID!
    $quantity: Int!
    $unit: VerificationUnit!
    $message: String
  ) {
    verification: requestVerification(
      postId: $postId
      quantity: $quantity
      unit: $unit
      message: $message
    ) {
      id
      post {
        id
        verifiedAt
        verification {
          id
          unit
          quantity
          insertedAt
        }
      }
    }
  }
`;

interface RequestVerificationModalProps {
  post: RequestVerificationModal_PostFragment;
  close: () => void;
}

type FormValues = VerificationFormValues & { message: string };

const RequestVerificationModal = ({
  post,
  close,
}: RequestVerificationModalProps) => {
  const [requestVerification] = useMutation<
    RequestVerificationMutation,
    RequestVerificationMutationVariables
  >(REQUEST_VERIFICATION_MUTATION);
  const onSubmit = async ({ unit, quantity, message }: FormValues) => {
    if (!unit) {
      return null;
    }
    return requestVerification({
      variables: {
        postId: post.id,
        unit,
        quantity: Number(quantity),
        message,
      },
    }).then(close);
  };

  return (
    <Modal width={515}>
      <ModalTitle
        subtitle="Verification ensures this post is accurate. Let the owner know why you’d like this post to be verified."
        close={close}
      >
        Request verification <VerifiedIcon inline size="large" />
      </ModalTitle>
      <div className={styles.label}>Post owner</div>
      <UserRow user={post.owner} />
      <hr className={styles.hr} />
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          ...FREQUENCIES[0],
          message: '',
        }}
        validate={validateVerificationFrequencyFields}
      >
        {({ values, setFieldValue, isValid, dirty }) => (
          <>
            <VerificationFrequencyFields
              frequency={values.frequency}
              setFieldValue={setFieldValue}
            />
            <Field
              className={styles.message}
              margin="none"
              name="message"
              type="textarea"
              label={`Give ${post.owner.name} context`}
              placeholder="Why are you requesting verification?"
              helpText="We’ll send a notification to the post owner asking them to
              verify."
            />
            <ModalActionFooter
              primary={
                <Submit size="large" disabled={dirty && !isValid}>
                  Request verification
                </Submit>
              }
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

RequestVerificationModal.fragments = { post: POST_FRAGMENT };

export default RequestVerificationModal;
